/**
 * Utilities for preserving case patterns during translation.
 */

/**
 * Case pattern detected in a word.
 * - 'lower': all lowercase (hello)
 * - 'upper': all uppercase (HELLO)
 * - 'capitalized': first letter uppercase (Hello)
 * - 'mixed': mixed case like camelCase (GitHub, iPhone)
 */
export type CasePattern = 'capitalized' | 'lower' | 'mixed' | 'upper';

/**
 * Applies a case pattern to a word, optionally using original word for mixed case.
 * Optimized: lowercase case returns word directly if already lowercase.
 */
export function applyCasePattern(word: string, pattern: CasePattern, original?: string): string {
  // Fast path for lowercase (most common case)
  // Check if word is already lowercase to avoid creating new string
  if (pattern === 'lower') {
    // Quick check: if first char is lowercase and word equals lowercase, return as-is
    const firstChar = word.codePointAt(0)!;
    if (firstChar >= 97 && firstChar <= 122 && word === word.toLowerCase()) {
      return word;
    }
    return word.toLowerCase();
  }

  switch (pattern) {
    case 'capitalized': {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }
    case 'mixed': {
      if (original !== undefined && original.length > 0) {
        return applyMixedCase(word, original);
      }
      return word.toLowerCase();
    }
    case 'upper': {
      return word.toUpperCase();
    }
    default: {
      return word.toLowerCase();
    }
  }
}

/**
 * Detects the case pattern of a word.
 * Optimized for the common case (lowercase) by checking it first.
 */
export function detectCasePattern(word: string): CasePattern {
  if (word.length === 0) {
    return 'lower';
  }

  // Fast path: check first character to quickly identify lowercase words
  // Most English text is lowercase, so this avoids expensive string operations
  const firstCode = word.codePointAt(0)!;
  const firstChar = word[0]!;
  const isFirstUpper =
    (firstCode >= 65 && firstCode <= 90) || // A-Z (fast ASCII path)
    (firstCode > 127 && firstChar !== firstChar.toLowerCase()); // Unicode (É, Ö, etc.)

  // All lowercase - most common case (codepoint check avoids string allocation)
  if (!isFirstUpper && isAllLowerCase(word)) {
    return 'lower';
  }

  // Single characters
  if (word.length === 1) {
    // "I" is treated as lowercase for translation purposes
    if (word === 'I') {
      return 'lower';
    }
    return isFirstUpper ? 'capitalized' : 'lower';
  }

  // Check if all uppercase
  if (word === word.toUpperCase()) {
    // Short all-caps words (2 letters like "UI", "AI") should be title-cased
    // These are typically initialisms (spelled out letter-by-letter)
    if (word.length <= 2) {
      return 'capitalized';
    }
    return 'upper';
  }

  // Capitalized: first uppercase, rest all lowercase
  if (isFirstUpper) {
    let restIsLower = true;
    for (let i = 1; i < word.length; i++) {
      const c = word.codePointAt(i)!;
      if (
        (c >= 65 && c <= 90) || // A-Z
        (c > 127 && word[i] !== word[i]!.toLowerCase()) // Unicode
      ) {
        restIsLower = false;
        break;
      }
    }
    if (restIsLower) {
      return 'capitalized';
    }
  }

  // Mixed case (like "GitHub", "iPhone", "McDonald")
  return 'mixed';
}

/**
 * Splits a word at camelCase boundaries.
 * e.g., "iCloud" -> ["i", "Cloud"], "MacBook" -> ["Mac", "Book"]
 */
export function splitCamelCase(word: string): null | string[] {
  // Fast path: need at least 2 chars for camelCase
  if (word.length < 2) {
    return null;
  }

  // Fast path: check for any internal uppercase (position 1+)
  // Uses charCode for speed - A-Z are 65-90
  let hasInternalUpper = false;
  for (let i = 1; i < word.length; i++) {
    const c = word.codePointAt(i)!;
    if (c >= 65 && c <= 90) {
      hasInternalUpper = true;
      break;
    }
  }
  if (!hasInternalUpper) {
    return null;
  }

  // Full camelCase split
  const parts: string[] = [];
  let start = 0;

  for (let i = 1; i < word.length; i++) {
    const prevCode = word.codePointAt(i - 1)!;
    const currCode = word.codePointAt(i)!;
    // Boundary: previous char is lowercase (a-z), current is uppercase (A-Z)
    if (prevCode >= 97 && prevCode <= 122 && currCode >= 65 && currCode <= 90) {
      parts.push(word.slice(start, i));
      start = i;
    }
  }

  if (parts.length > 0) {
    parts.push(word.slice(start));
    return parts;
  }

  return null;
}

/**
 * Applies mixed case from original word to translated word.
 * Uses position-based mapping for each character.
 */
function applyMixedCase(translated: string, original: string): string {
  const lowerTranslated = translated.toLowerCase();
  let result = '';

  let i = 0;
  for (const char of lowerTranslated) {
    // Use original's case pattern if within bounds, otherwise lowercase
    if (i < original.length) {
      const origChar = original[i]!;
      result += origChar === origChar.toUpperCase() ? char.toUpperCase() : char;
    } else {
      result += char;
    }
    i++;
  }

  return result;
}

/** Checks if all characters are lowercase without allocating a new string. */
function isAllLowerCase(word: string): boolean {
  for (let i = 0; i < word.length; i++) {
    const c = word.codePointAt(i)!;
    if (c >= 65 && c <= 90) {
      return false;
    } // ASCII uppercase A-Z
    if (c > 127 && word[i] !== word[i]!.toLowerCase()) {
      return false;
    } // Unicode
  }
  return true;
}
